import { useState, useEffect, useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  startOfDay,
  addMinutes,
  setHours,
  setMinutes,
  startOfWeek,
  endOfWeek,
  isToday,
  parse,
  addDays,
} from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";

type TimeRange = {
  start_time: string;
  end_time: string;
};

type DayAvailability = {
  [key: string]: TimeRange[];
};

type CalendarProps = {
  employeeAvailabilityHours: DayAvailability | null;
  appointmentDuration: number;
  setSelectedDateTime: (date: Date | null) => void;
  selectedEmployee: any;
};

type BookedSlot = {
  id: number;
  employee_id: number;
  appointment_time: string;
  duration: number;
};

export const Calendar: React.FC<CalendarProps> = ({
  employeeAvailabilityHours,
  appointmentDuration,
  setSelectedDateTime,
  selectedEmployee,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);

  const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
  const branchTimezone =
    userData?.selectedBranch?.timezone || "America/Chicago";

  const availabilityHours = employeeAvailabilityHours;

  const fetchBookedSlots = async () => {
    if (!selectedEmployee) return;

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_APP_API_BASE_URL}api/v1/appointment/booked_slots/`,
        {
          params: {
            employee_id: selectedEmployee.id,
            start_date: format(new Date(), "yyyy-MM-dd"),
          },
        },
      );

      const convertedBookedSlots = response.data.map((slot: BookedSlot) => {
        const branchDate = toZonedTime(
          new Date(slot.appointment_time),
          branchTimezone,
        );
        return {
          ...slot,
          appointment_time: formatInTimeZone(
            branchDate,
            branchTimezone,
            "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
          ),
        };
      });
      setBookedSlots(convertedBookedSlots);
    } catch (error) {
      console.error("Error fetching booked slots:", error);

      setBookedSlots([]);
    }
  };

  useEffect(() => {
    fetchBookedSlots();
  }, [selectedEmployee, currentDate, selectedDate]);

  const goToPreviousMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentDate));
    const end = endOfWeek(endOfMonth(currentDate));
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const hasAvailableSlots = (date: Date): boolean => {
    if (date < startOfDay(new Date()) && !isToday(date)) {
      return false;
    }

    const dayOfWeek = format(date, "EEEE");
    if (!availabilityHours?.[dayOfWeek]) {
      return false;
    }

    return true;
  };

  const getAvailableTimeSlots = (date: Date): string[] => {
    const slots: string[] = [];
    const dayOfWeek = format(date, "EEEE");
    const daySlots = availabilityHours?.[dayOfWeek] || [];
    const now = new Date();

    daySlots.forEach((slot) => {
      const [startHour, startMinute] = slot.start_time.split(":").map(Number);
      let currentTime = setMinutes(setHours(date, startHour), startMinute);

      const [endHour, endMinute] =
        slot.end_time === "24:00"
          ? [24, 0]
          : slot.end_time.split(":").map(Number);

      const endTime =
        endHour === 24
          ? addDays(setMinutes(setHours(date, 0), 0), 1)
          : setMinutes(setHours(date, endHour), endMinute);

      const lastPossibleStart = addMinutes(endTime, -appointmentDuration);

      const minutesToAdd = startMinute % 15 === 0 ? 0 : 15 - (startMinute % 15);

      currentTime = addMinutes(currentTime, minutesToAdd);

      while (currentTime <= lastPossibleStart) {
        if (isToday(date) && currentTime < now) {
          currentTime = addMinutes(currentTime, 15);
          continue;
        }

        const isBooked =
          Array.isArray(bookedSlots) &&
          bookedSlots.some((bookedSlot) => {
            const bookedTime = new Date(bookedSlot.appointment_time);
            const bookedStart = new Date(bookedTime);
            const slotStart = new Date(currentTime);
            const slotEnd = addMinutes(slotStart, appointmentDuration);

            return (
              bookedSlot.employee_id === selectedEmployee?.id &&
              isSameDay(currentTime, bookedTime) &&
              ((slotStart >= bookedStart &&
                slotStart < addMinutes(bookedStart, bookedSlot.duration - 1)) ||
                (slotEnd > addMinutes(bookedStart, 1) &&
                  slotEnd <= addMinutes(bookedStart, bookedSlot.duration)) ||
                (slotStart <= bookedStart &&
                  slotEnd >= addMinutes(bookedStart, bookedSlot.duration)))
            );
          });

        if (!isBooked) {
          slots.push(format(currentTime, "HH:mm"));
        }

        currentTime = addMinutes(currentTime, 15);
      }
    });

    return slots.sort();
  };

  const handleDateClick = (date: Date) => {
    if (hasAvailableSlots(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
    }
  };

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const [hours, minutes] = time.split(":").map(Number);

      const dateString = format(selectedDate, "yyyy-MM-dd");
      const timeString = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:00`;
      const isoString = `${dateString}T${timeString}`;
      const localTime = new Date();
      const branchTime = toZonedTime(localTime, branchTimezone);

      const timeDiff = branchTime.getTime() - localTime.getTime();
      const branchDate = new Date(isoString);
      const adjustedDate = new Date(branchDate.getTime() - timeDiff);

      setSelectedDateTime(adjustedDate);
    }
  };

  return (
    <div className="relative w-full">
      <div
        className={`max-h-96 min-h-96 min-w-full max-w-full ${
          !selectedEmployee ? "cursor-not-allowed opacity-70" : ""
        }`}
      >
        {/* Calendar Header */}
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={goToPreviousMonth}
            className="rounded p-2 hover:bg-gray-100"
            disabled={!selectedEmployee}
          >
            &lt;
          </button>
          <h2 className="text-lg font-semibold">
            {format(currentDate, "MMMM yyyy")}
          </h2>
          <button
            type="button"
            onClick={goToNextMonth}
            className="rounded p-2 hover:bg-gray-100"
            disabled={!selectedEmployee}
          >
            &gt;
          </button>
        </div>

        {!selectedEmployee && (
          <p className="text-sm text-red-500 opacity-100">
            Please select an employee first
          </p>
        )}

        {/* Calendar Grid */}
        <div className="overflow-hidden rounded-lg border">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 bg-gray-50">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((date) => {
              const isAvailable = hasAvailableSlots(date);
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const isCurrentMonth = isSameMonth(date, currentDate);

              return (
                <button
                  type="button"
                  key={date.toString()}
                  onClick={() => handleDateClick(date)}
                  disabled={!isAvailable || !selectedEmployee}
                  className={`relative h-12 border-r border-t p-2 ${!isCurrentMonth ? "text-gray-400" : ""} ${isSelected ? "bg-red-50" : ""} ${isAvailable ? "hover:bg-gray-50" : ""} ${!selectedEmployee ? "cursor-not-allowed" : ""} `}
                >
                  <span className={isToday(date) ? "text-red-500" : ""}>
                    {format(date, "d")}
                  </span>
                  {isAvailable && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                      <FontAwesomeIcon
                        icon={faCircle}
                        className="text-[6px] text-red-500"
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slot Selection */}
        {selectedDate && (
          <div className="absolute left-0 top-0 h-full w-full rounded-lg border bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {format(selectedDate, "EEEE, MMMM d")}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedDate(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto">
              {getAvailableTimeSlots(selectedDate).map((time) => (
                <button
                  type="button"
                  key={time}
                  onClick={() => handleTimeClick(time)}
                  className={`rounded-md p-2 text-sm transition-colors ${selectedTime === time ? "bg-red-500 text-white" : "bg-gray-100 hover:bg-gray-200"} `}
                >
                  {format(parse(time, "HH:mm", selectedDate), "h:mm a")} (
                  {branchTimezone})
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
